import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  Film, 
  ShieldCheck, 
  Users, 
  BarChart3, 
  Award, 
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  Building2,
  Zap,
  Target,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const Home = ({ onDemoClick }) => {
  const valueProps = [
    {
      icon: GraduationCap,
      title: 'Plataforma EAD personalizada',
      description: 'LMS completo com sua marca, domínio próprio e funcionalidades sob medida.'
    },
    {
      icon: Users,
      title: 'Trilhas e Turmas',
      description: 'Organize treinamentos em trilhas de aprendizagem e gerencie turmas facilmente.'
    },
    {
      icon: Award,
      title: 'Certificados Automáticos',
      description: 'Emita certificados personalizados automaticamente ao concluir cursos.'
    },
    {
      icon: BarChart3,
      title: 'Relatórios Completos',
      description: 'Dashboards com métricas de desempenho, engajamento e progresso dos colaboradores.'
    },
    {
      icon: Film,
      title: 'Produção Profissional',
      description: 'Estúdio próprio para gravação e edição de cursos com qualidade cinematográfica.'
    },
    {
      icon: ShieldCheck,
      title: 'NR-01 Psicossocial',
      description: 'Ferramenta de apoio para gestão e organização dos requisitos da NR-01.'
    },
  ];

  const solutions = [
    {
      title: 'Plataforma EAD',
      subtitle: 'Faculdade Corporativa',
      description: 'Plataforma completa de ensino a distância personalizada para sua empresa.',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop',
      path: '/solucoes/plataforma-ead',
      color: 'blue'
    },
    {
      title: 'Produção de Cursos',
      subtitle: 'Estúdio + Edição',
      description: 'Criação de conteúdo educacional com produção profissional de vídeos.',
      image: 'https://images.unsplash.com/photo-1769699167679-a3184af4d8f1?w=600&h=400&fit=crop',
      path: '/solucoes/producao-cursos',
      color: 'orange'
    },
    {
      title: 'NR-01 Psicossocial',
      subtitle: 'Conformidade',
      description: 'Apoio na organização e gestão dos requisitos de saúde mental no trabalho.',
      image: 'https://images.unsplash.com/photo-1646227655718-dd721b681d91?w=600&h=400&fit=crop',
      path: '/solucoes/nr01',
      color: 'green'
    },
  ];

  const steps = [
    { number: '01', title: 'Diagnóstico', description: 'Entendemos suas necessidades e objetivos de treinamento.' },
    { number: '02', title: 'Implantação', description: 'Configuramos a plataforma personalizada com sua identidade.' },
    { number: '03', title: 'Produção', description: 'Criamos ou migramos seu conteúdo com qualidade profissional.' },
    { number: '04', title: 'Publicação', description: 'Seus cursos ficam disponíveis para todos os colaboradores.' },
    { number: '05', title: 'Relatórios', description: 'Acompanhe métricas e resultados em tempo real.' },
  ];

  const benefits = [
    'Redução de custos com treinamentos presenciais',
    'Padronização do conhecimento corporativo',
    'Escalabilidade para treinar equipes em qualquer lugar',
    'Métricas precisas de desempenho e engajamento',
    'Conformidade com NR-01 e outras regulamentações',
    'Flexibilidade para colaboradores aprenderem no próprio ritmo',
  ];

  const testimonials = [
    {
      quote: 'A plataforma da CSP transformou nossa universidade corporativa. O suporte é excepcional e os resultados superaram as expectativas.',
      author: 'Maria Silva',
      role: 'Diretora de RH',
      company: 'Empresa ABC',
    },
    {
      quote: 'A qualidade da produção dos cursos é impressionante. Nossa equipe engajou muito mais com os novos conteúdos.',
      author: 'João Santos',
      role: 'Gerente de T&D',
      company: 'Indústria XYZ',
    },
    {
      quote: 'O suporte para NR-01 nos ajudou a organizar todo o processo de conformidade de forma clara e eficiente.',
      author: 'Ana Oliveira',
      role: 'Coordenadora de SST',
      company: 'Grupo 123',
    },
  ];

  const faqs = [
    {
      question: 'Quanto tempo leva para implementar a plataforma EAD?',
      answer: 'O tempo de implementação varia de acordo com a complexidade do projeto. Em média, uma plataforma básica pode estar operacional em 2 a 4 semanas. Projetos mais complexos, com integrações e personalizações específicas, podem levar de 6 a 12 semanas.'
    },
    {
      question: 'A plataforma funciona em dispositivos móveis?',
      answer: 'Sim! Nossa plataforma é totalmente responsiva e funciona perfeitamente em smartphones, tablets e computadores. Também oferecemos aplicativo mobile opcional para uma experiência ainda melhor.'
    },
    {
      question: 'Vocês produzem o conteúdo dos cursos?',
      answer: 'Sim! Temos estúdio próprio e equipe especializada para produção completa de cursos, incluindo roteirização, gravação, edição e motion graphics. Também podemos trabalhar com seu conteúdo existente.'
    },
    {
      question: 'O que está incluído no suporte para NR-01?',
      answer: 'Oferecemos uma ferramenta que auxilia na organização do processo de gestão de riscos psicossociais, incluindo questionários, relatórios e dashboards. A validação final e responsabilidade técnica permanecem com o profissional habilitado da empresa.'
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-orange-50/20" />
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231E88E5' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                Líder em Educação Corporativa
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Faculdade Corporativa{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">
                  pronta
                </span>{' '}
                para sua empresa treinar com{' '}
                <span className="text-orange-500">escala</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                Plataforma EAD + Produção de Cursos + NR-01 Psicossocial em um só parceiro. 
                Transforme a capacitação da sua equipe com tecnologia e qualidade profissional.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onDemoClick}
                  data-testid="hero-demo-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-xl shadow-orange-500/25 transition-all hover:-translate-y-1"
                >
                  Solicitar Demonstração
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  data-testid="hero-specialist-btn"
                  className="px-8 py-6 rounded-full text-lg font-medium border-2 border-slate-200 hover:border-blue-500 hover:text-blue-600"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Falar com Especialista
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-4">Empresas que confiam na CSP:</p>
                <div className="flex items-center gap-8 opacity-50">
                  <Building2 className="h-8 w-8 text-slate-400" />
                  <Building2 className="h-8 w-8 text-slate-400" />
                  <Building2 className="h-8 w-8 text-slate-400" />
                  <Building2 className="h-8 w-8 text-slate-400" />
                </div>
              </div>
            </motion.div>

            {/* Right Column - Platform Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-900/10 overflow-hidden border border-slate-100">
                <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-400"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                    <span className="w-3 h-3 rounded-full bg-green-400"></span>
                  </div>
                  <span className="text-xs text-slate-500 ml-2">universidade.suaempresa.com.br</span>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1758519290311-7e6ddb194016?w=800&h=500&fit=crop"
                  alt="Plataforma CSP"
                  className="w-full"
                />
              </div>
              
              {/* Floating Cards */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">95%</p>
                    <p className="text-xs text-slate-500">Taxa de conclusão</p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">10k+</p>
                    <p className="text-xs text-slate-500">Colaboradores</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section data-testid="value-props-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tudo que sua empresa precisa em{' '}
              <span className="text-blue-600">um só lugar</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Soluções completas para educação corporativa, da plataforma à produção de conteúdo.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={stagger}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {valueProps.map((prop, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full p-6 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                      <prop.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{prop.title}</h3>
                    <p className="text-slate-600">{prop.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section data-testid="solutions-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Nossas <span className="text-orange-500">Soluções</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Escolha a solução ideal ou combine todas para uma experiência completa.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={solution.path} data-testid={`solution-card-${index}`}>
                  <Card className="h-full overflow-hidden group hover:shadow-xl transition-all duration-300 border-0 bg-white">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <span className="text-xs font-medium text-white/80">{solution.subtitle}</span>
                        <h3 className="text-xl font-bold text-white">{solution.title}</h3>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-slate-600 mb-4">{solution.description}</p>
                      <span className="inline-flex items-center text-blue-600 font-medium group-hover:gap-2 transition-all">
                        Saiba mais <ArrowRight className="ml-1 h-4 w-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section data-testid="how-it-works-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Como <span className="text-blue-600">Funciona</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Um processo simples e transparente para transformar sua educação corporativa.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-slate-200" />

            <div className="space-y-12 lg:space-y-0">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  className={`relative lg:flex items-center gap-8 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                    <div className={`inline-flex flex-col ${index % 2 === 0 ? 'lg:items-end' : 'lg:items-start'}`}>
                      <span className="text-5xl font-bold text-slate-100 mb-2">{step.number}</span>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-slate-600 max-w-sm">{step.description}</p>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-blue-600 border-4 border-white shadow" />

                  <div className="lg:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section data-testid="benefits-section" className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Benefícios para sua{' '}
                <span className="text-orange-400">empresa</span>
              </h2>
              <p className="text-slate-400 mb-8 text-lg">
                Invista em educação corporativa e colha resultados mensuráveis para o negócio.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Equipe em treinamento"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-orange-500 rounded-2xl p-6 shadow-xl">
                <div className="text-center">
                  <p className="text-4xl font-bold">40%</p>
                  <p className="text-sm text-orange-100">Redução de custos</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              O que nossos <span className="text-blue-600">clientes</span> dizem
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Histórias reais de empresas que transformaram sua educação corporativa.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full p-6 border border-slate-100 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-6 italic">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-lg font-semibold text-slate-600">
                          {testimonial.author.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{testimonial.author}</p>
                        <p className="text-sm text-slate-500">
                          {testimonial.role}, {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="faq-section" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Perguntas <span className="text-orange-500">Frequentes</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-slate-100 px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 md:py-32 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Pronto para transformar a educação da sua empresa?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Solicite uma demonstração gratuita e descubra como a CSP pode ajudar sua empresa a crescer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={onDemoClick}
                data-testid="cta-demo-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-xl transition-all hover:-translate-y-1"
              >
                Solicitar Demonstração Gratuita
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                data-testid="cta-contact-btn"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 rounded-full text-lg font-medium"
              >
                Falar com Especialista
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
